import {
  ProfileContestantModel,
  ProfileModel,
  StartupModel,
  UserChangePasswordModel,
  UserModel,
  UserProfileModel,
  UserTypes,
  UserUpdatePayload
} from '@/models/user.model';
import {Observable, of} from 'rxjs';
import {catchError, map, retry, shareReplay} from 'rxjs/operators';
import {TablePlatformSettingsModel} from '@/models/platform-settings.model';
import {AuthenticatedModel} from '@/models/authenticated.model';
import {ChallengeModel, ChallengeProblemLocation} from '@/models/challenge.model';
import {axiosInstance, axiosInstanceAuthenticated} from '@/api/axios.config.api';
import {BaseApi, PaginatedParams, PaginatedResponse, RequireAuthApi} from '@/api/base.api';
import {ActivityModel} from '@/models/activity.model';
import {authState, userState} from '@/utils/store-acessor';

export class UserApi extends BaseApi<UserModel> {
  constructor() {
    super('', [
      RequireAuthApi.CREATE,
      RequireAuthApi.UPDATE,
      RequireAuthApi.DESTROY,
    ])
  }

  protected readonly challengesUrl = `${this.url}/challenges`;
  protected readonly profileUrl = `${this.url}/profile`;

  me(): Observable<UserModel> {
    return axiosInstanceAuthenticated()
      .get<UserModel>(`${this.url}/me`)
      .pipe(
        map((response) => response.data),
        catchError((e) => {
          if (e.message.includes('401')) {
            authState.logout();
          }
          return of(e)
        }),
      )

  }

  createUser(data: UserModel): Observable<AuthenticatedModel> {
    return axiosInstance().post<AuthenticatedModel>(`users`, data)
      .pipe(
        map((response) => {
          localStorage.setItem('credentials', JSON.stringify(response.data));
          return response.data
        })
      )
  }

  findBySlug(slug: string): Observable<UserModel> {
    return axiosInstance().get<UserModel>(`users/${slug}`)
      .pipe(
        map((response) => response.data),
      )
  }

  findById(id: number): Observable<UserModel> {
    return axiosInstance().get<UserModel>(`users/${id}`)
      .pipe(
        map((response) => response.data),
      )
  }

  registerProfile(user_id: number, data: ProfileContestantModel):
    Observable<{ user_profile_id: number, contestant_id: number }> {
    return axiosInstance().post<{ user_profile_id: number, contestant_id: number }>
    (`${this.url}/register/users/${user_id}`, data)
      .pipe(
        map((res) => res.data),
      )
  }

  registerStartup(data: StartupModel): Observable<any> {
    return axiosInstance().post(this.url, data).pipe(retry(1))
  }

  registerPlatformSettings(id: number, data: TablePlatformSettingsModel): Observable<any> {
    return axiosInstance().post(`users/${id}/platform-settings`, data)
      .pipe(
        map((response) => response.data),
      );
  }

  registerSkills(data: any): Observable<any> {
    return axiosInstance().post(this.url, data)
      .pipe(
        retry(1),
        map((response) => response.data),
      )
  }

  myChallenges(slug: string): Observable<ChallengeModel[]> {
    return axiosInstanceAuthenticated()
      .get<ChallengeModel[]>(`${this.url}/${slug}/challenges`)
      .pipe(
        map((response) => response.data)
      )
  }

  updateData(newData: UserUpdatePayload) {
    return axiosInstanceAuthenticated()
      .put<UserUpdatePayload>(`${this.url}/me`, newData)
      .pipe(
        map((response) => response.data)
      );
  }

  createProfile(data: ProfileModel): Observable<UserProfileModel> {
    return axiosInstanceAuthenticated()
      .post<UserProfileModel>(this.profileUrl, data)
      .pipe(
        map((response) => response.data),
      );
  }

  updateProfile(data: ProfileModel): Observable<UserProfileModel> {
    return axiosInstanceAuthenticated()
      .put<UserProfileModel>(this.profileUrl, data)
      .pipe(
        map((response) => response.data),
      );
  }

  getChallenges(params: PaginatedParams,
                userRoles: UserTypes[] = []
  ): Observable<PaginatedResponse<ChallengeModel>> {
    if (!userState.canAcceptChallenges) {
      return of();
    }

    let filter: string = '';
    if (userRoles.length > 0) {
      userRoles.forEach((v) => {
        if (!!v) {
          filter += `${v},`;
        }
      });
    } else {
      filter = 'false'
    }
    return axiosInstanceAuthenticated()
      .get<PaginatedResponse<ChallengeModel>>(
        `${this.url}/challenges/my`, {
          params: {...params, 'filter[role]': filter}
        })
      .pipe(
        shareReplay(),
        map((response) => response.data),
      );
  }

  getSubscriptions(slug: string): Observable<PaginatedResponse<ChallengeModel>> {
    return axiosInstance()
      .get<PaginatedResponse<ChallengeModel>>(`${this.url}/users/${slug}/following`)
      .pipe(
        map((response) => response.data),
      );
  }

  acceptChallenge(challengeId: number): Observable<ChallengeModel[]> {
    return axiosInstanceAuthenticated()
      .post(`${this.challengesUrl}/${challengeId}/accept`)
      .pipe(
        map((response) => response.data),
      );
  }

  abortChallenge(challengeId: number): Observable<ChallengeModel[]> {
    return axiosInstanceAuthenticated()
      .post(`${this.challengesUrl}/${challengeId}/abort`)
      .pipe(
        map((response) => response.data),
      );
  }

  follow(id: number): Observable<any> {
    const route = `${this.challengesUrl}/${id}/follow`;
    return axiosInstanceAuthenticated().post(route, {})
      .pipe(map((response) => response.data));
  }

  unfollow(id: number): Observable<any> {
    const route = `${this.challengesUrl}/${id}/unfollow`;
    return axiosInstanceAuthenticated().post(route, {})
      .pipe(map((response) => response.data));
  }

  changePasswordOrEmail(data: UserChangePasswordModel): Observable<any> {
    const url = `${this.url}/contestants/me/password`;
    return axiosInstance().put(url, data)
      .pipe(map((response) => response.data));
  }

  sendLocaleWithSameProblem(challengeId: number, data: ChallengeProblemLocation): Observable<ChallengeProblemLocation> {
    return axiosInstanceAuthenticated().post(`${this.challengesUrl}/${challengeId}/problem-locations`, data)
      .pipe(map((response) => response.data));
  }

  searchUsersToInvite(data: {
    challengeId: number,
    skills?: string[],
    residenceId?: number,
    page?: number,
    teamId?: number
  }): Observable<PaginatedResponse<UserModel>> {
    let url = `${this.url}/users/search/challenge/${data.challengeId}/teams`;
    if (data.teamId) {
      url += `/${data.teamId}`
    }
    let skills: string = '';
    data.skills?.forEach((skill, index) => {
      skills += `${skill},`
    });
    return axiosInstanceAuthenticated().get<PaginatedResponse<UserModel>>(
      url, {
        params: {
          'page': data.page,
          'filter[skills]': data.skills,
          'filter[country-residence]': data.residenceId
        }
      })
      .pipe(map((response) => response.data))
  }

  fetchActivities(slug: string): Observable<PaginatedResponse<ActivityModel>> {
    return axiosInstance().get<PaginatedResponse<ActivityModel>>(`${this.url}/users/${slug}/activities`)
      .pipe(map((response) => response.data))
  }

  followedChallenges(slug: string, params: PaginatedParams): Observable<PaginatedResponse<ChallengeModel>> {
    return axiosInstance().get<PaginatedResponse<ChallengeModel>>(`${this.url}/users/${slug}/following`, {params})
      .pipe(map((response) => response.data))
  }
}
